import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ensureUidsDeep, genUid } from './uid.js';
import { toSalesforce } from './toSalesforce.js';
import { fromSalesforce } from './fromSalesforce.js';
import { toPlanOrder } from './toPlanOrder.js';
import { fromPlanOrder } from './fromPlanOrder.js';
import { resolveHydration } from './hydration.js';
import {
  versionOfBody,
  emptyVersions,
  confirmVersion,
  addUnconfirmed,
  saveUrl,
} from './pqVersion.js';
import { pqFormSlice, diffPq } from './pqDiff.js';
import { appendLogEntries } from './planningLog.js';
import {
  changedPaths,
  buildCandidate,
  getAtPath,
  pathKey,
  labelForPath,
  isolateBadPaths,
} from './saveQuarantine.js';
import { labelForKey } from './pqDiff.js';
import { getUserIdentity } from '../utils/identity.js';
import {
  classifySaveError,
  SAVE_MSG_NAME_FIRST,
  SAVE_MSG_NAME_LAST,
  SAVE_MSG_NAME_BOTH,
} from '../utils/validation.js';
import { trackEvent } from '../utils/telemetry.js';

export const STORE_KEY = 'pfa-intake-v2';
const AUTOSAVE_TICK_MS = 10000;
const SAVED_FLASH_MS = 2000;
// How long a deleted row stays undoable (toast visible + Ctrl/Cmd+Z active).
const UNDO_WINDOW_MS = 6000;
// Retry backoff for failed autosaves: 10s, 20s, 40s, ... capped at 5 minutes.
// The first retry stays quick (a transient blip recovers on the next tick);
// a persistent outage stops hammering Salesforce every 10 seconds.
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const backoffMs = failures =>
  Math.min(AUTOSAVE_TICK_MS * 2 ** Math.max(failures - 1, 0), MAX_BACKOFF_MS);

// Demo build (standalone host, no Salesforce backend). When true the app runs
// entirely on localStorage: no SF sync loop, no manual SF save, and no
// save-failed / no-reference banners. Set via VITE_DEMO=true at build time
// (see the Azure demo deploy workflow).
export const DEMO_MODE = import.meta.env.VITE_DEMO === 'true';

// Deployment environment marker, defined once in src/env.js. Staging and dev are
// full auth-gated builds (same as production) that point at a Salesforce
// SANDBOX through their own Function Apps, so each MUST be visually distinct
// from prod or someone will edit sandbox data thinking it is live (or the
// reverse). Re-exported here because the top bar and screens already import it
// from the store.
import { APP_ENV, IS_STAGING, IS_DEV_ENV, DEV_ONLY } from '../env.js';
export { APP_ENV, IS_STAGING, IS_DEV_ENV };

// Top-level store keys that belong to the PQ form. Plan Order Form data
// (planOrder, _caseId, _workflow) is deliberately excluded so edits in
// Plan Mode don't trigger PQ autosaves.
const PQ_SLICE_KEYS = [
  'family',
  'contact',
  'employment',
  'goals',
  'relationships',
  'assets',
  'liabilities',
  'insurance',
  'income',
  'taxesExpenses',
  '_flags',
  '_assetChecks',
  '_uid',
];

// Stable serialization of what a save would actually SEND. Used to detect
// whether anything in the PQ has changed since the last successful sync.
//
// This is computed from the toSalesforce payload, not the raw store, and the
// difference is the fix for the spurious save-on-open: the auto-source syncs
// mutate rows at load time (seeding, prefix-healing, `_`-prefixed bookkeeping
// keys), so a freshly loaded record's raw store never matched what was loaded,
// the digest changed, and the autosave loop POSTed with nobody typing. Every
// open wrote to Salesforce — the mechanism behind planning's "it seems to auto
// save" fear. The payload ignores bookkeeping keys by construction, so "dirty"
// now means precisely "the POST body would differ from the last one sent".
//
// Safe because toSalesforce is deterministic (no timestamps, no generated
// ids — verified before switching). The raw-slice fallback only covers a
// payload build crashing on malformed data; returning the exact bytes matters
// less there than returning something stable.
function pqDigest(data) {
  if (!data) return '';
  try {
    return JSON.stringify(toSalesforce(data));
  } catch {
    const slice = {};
    for (const k of PQ_SLICE_KEYS) {
      if (data[k] !== undefined) slice[k] = data[k];
    }
    try {
      return JSON.stringify(slice);
    } catch {
      return '';
    }
  }
}

const StoreContext = createContext(null);

function readFromStorage() {
  try {
    // The Lead ID, or the household ID when the PQ was launched from a
    // household with no lead. Either one is the record this tab is about, so
    // either one switching means a different household is on screen.
    const urlLeadId = getRecordIdFromUrl();
    const urlCaseId = getCaseIdFromUrl();

    // View and planning modes are fully ephemeral: they neither read nor write
    // localStorage. STORE_KEY is a single global slot, not keyed per household,
    // so the normal path DELETES whatever is cached when the URL names a
    // different lead. A planner opening someone else's PQ must not wipe the
    // draft they have in progress, and must not leave another household's PII
    // sitting in their browser afterwards.
    if (isEphemeralSession()) {
      const fresh = {};
      if (urlLeadId) fresh._uid = urlLeadId;
      if (urlCaseId) fresh._caseId = urlCaseId;
      return ensureRootUid(fresh);
    }

    const stored = ensureUidsDeep(JSON.parse(localStorage.getItem(STORE_KEY) || '{}'));

    // If the URL points to a different lead or case than what is cached in
    // localStorage, the user has navigated to a different household. Discard
    // stale local data immediately so no previous client's fields ever appear,
    // even during the loading-spinner phase or on a Salesforce load failure.
    const storedUid = stored._uid || '';
    const storedCaseId = stored._caseId || '';
    const switchedIdentity =
      (urlLeadId && urlLeadId !== storedUid) ||
      (urlCaseId && urlCaseId !== storedCaseId);

    if (switchedIdentity) {
      // Different household: the old synced marker belongs to the previous
      // record and must not influence the new one's hydration decision.
      try { localStorage.removeItem(SYNCED_DIGEST_KEY); } catch { /* ignore */ }
      const fresh = {};
      if (urlLeadId) fresh._uid = urlLeadId;
      if (urlCaseId) fresh._caseId = urlCaseId;
      return ensureRootUid(fresh);
    }

    // Same identity as cached — preserve local edits with URL identifiers
    // as authoritative overrides. Drop any stale _caseId if the URL has
    // none (e.g. opening the PQ standalone after a Salesforce session).
    let data = stored;
    if (urlLeadId) data = { ...data, _uid: urlLeadId };
    if (urlCaseId) {
      data = { ...data, _caseId: urlCaseId };
    } else if (data._caseId) {
      const { _caseId, ...rest } = data;
      data = rest;
    }
    return ensureRootUid(data);
  } catch {
    return ensureRootUid({});
  }
}

function writeToStorage(data) {
  if (isEphemeralSession()) return; // view/planning leave no trace on the device
  localStorage.setItem(STORE_KEY, JSON.stringify(data));
}

// A companion marker: the pqDigest last CONFIRMED saved to Salesforce. Unlike
// the in-memory lastSyncedDigestRef (which resets to the local data on every
// reload), this survives a reload, so a fresh mount can tell whether the browser
// holds edits that never synced and must not be clobbered by the Salesforce
// load. See resolveHydration.
const SYNCED_DIGEST_KEY = STORE_KEY + ':syncedDigest';
function readSyncedDigest() {
  try {
    return localStorage.getItem(SYNCED_DIGEST_KEY) || '';
  } catch {
    return '';
  }
}
function writeSyncedDigest(digest) {
  if (isEphemeralSession()) return; // see writeToStorage
  try {
    localStorage.setItem(SYNCED_DIGEST_KEY, digest);
  } catch {
    /* ignore */
  }
}

// --- Cross-tab writer election ---
// Two tabs opened on the same lead used to autosave independently and silently
// clobber each other (the Salesforce record has no version/ETag, so the POST is
// last-write-wins). We elect ONE writer per household via the Web Locks API:
// every tab viewing the same lead contends for this one named lock, and only
// its holder syncs to Salesforce. The lock is held for the tab's lifetime and
// the browser auto-releases it on close or crash, so there are no stale locks to
// clean up and a waiting tab is promoted the moment the writer goes away.
const WRITER_LOCK_PREFIX = STORE_KEY + ':writer:';
function writerLockName(identity) {
  return WRITER_LOCK_PREFIX + (identity || 'anon');
}
// Guards the election effect against React StrictMode's double-invoke (and any
// re-mount): the provider lives for the whole tab, so the election must run
// exactly once. Never reset — a fresh tab is a fresh module load.
let writerElectionRan = false;

// Save the PQ to Salesforce. Returns { ok, result?, error? }:
//   ok:true  + result = parsed response ({ financialProfileId, leadId, ... })
//              + version = which save Salesforce now holds (see pqVersion.js)
//   ok:false + error  = a human-readable message (HTTP 4xx/5xx, success:false,
//                       or a network failure)
//   ok:false + conflict = another page or device saved since this page last
//                       looked, so this save was refused rather than let it
//                       erase that work
// Split from saveToSalesforce so the quarantine search can send a candidate
// payload it has assembled itself, rather than one derived from store data.
// `versions` is the page's pqVersion state; omitted, the save carries none.
async function postPayload(payload, versions = emptyVersions()) {
  try {
    const response = await fetch(saveUrl(versions), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { raw: text };
    }
    // Checked first: a refused stale save is not a problem with the data, and
    // must not start the quarantine search, which would probe field by field
    // against a record this page has not seen.
    if (response.status === 409 && body && body.conflict) {
      return { ok: false, conflict: true, status: 409, error: body.error || 'Saved elsewhere' };
    }
    // Salesforce refusing the DATA is signalled by `success:false` in the body,
    // and that can arrive with any status: 200 for a validation failure, 500
    // when the Apex JSON parser cannot deserialize a value. Either way the
    // transport worked and the verdict is about what we sent, so this is
    // checked BEFORE the status.
    //
    // Reading the status first is what went wrong on 2026-09-18: a percent sign
    // in a number field came back as 500 with `success:false`, was classified a
    // system fault, and so told the advisor to contact IT about a value she
    // could have fixed herself — and, worse, held the quarantine closed, which
    // would otherwise have isolated the one cell and saved the rest of her
    // morning's work.
    if (body && body.success === false) {
      console.error('Salesforce save failed:', body);
      return {
        ok: false,
        error: body.error || 'Save failed',
        status: response.status,
        rejectedByServer: true,
      };
    }
    if (!response.ok) {
      const msg =
        body?.error || body?.[0]?.message || `Save failed (HTTP ${response.status})`;
      console.error('Salesforce save failed:', body);
      return { ok: false, error: msg, status: response.status };
    }
    return { ok: true, result: body, version: body?.pqVersion || null };
  } catch (err) {
    console.error('Salesforce save error:', err);
    return { ok: false, error: err.message || 'Network error — could not reach Salesforce' };
  }
}

async function saveToSalesforce(data) {
  return postPayload(toSalesforce(data));
}

// Merge Salesforce-returned ids (financialProfileId / leadId) into the store
// for reference. These keys are NOT in PQ_SLICE_KEYS, so persisting them does
// not mark the PQ dirty or trigger a re-sync.
function applyPqIds(setData, result) {
  if (!result || typeof result !== 'object') return;
  const fpId = result.financialProfileId ? String(result.financialProfileId) : '';
  const leadId = result.leadId ? String(result.leadId) : '';
  if (!fpId && !leadId) return;
  setData(prev => {
    if (prev._financialProfileId === fpId && prev._leadId === leadId) return prev;
    const next = {
      ...prev,
      ...(fpId ? { _financialProfileId: fpId } : {}),
      ...(leadId ? { _leadId: leadId } : {}),
    };
    writeToStorage(next);
    return next;
  });
}

// Read a ?pqid= value from the current URL, if present.
function getPqidFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('pqid') || params.get('pqId');
    return id ? id.trim() : '';
  } catch {
    return '';
  }
}

// Read a ?leadId= value from the current URL, if present. This is the
// Salesforce Lead ID used when the PQ is launched from Salesforce.
function getLeadIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('leadId') || params.get('leadid');
    return id ? id.trim() : '';
  } catch {
    return '';
  }
}

// Read a ?householdId= value from the current URL, if present. The Salesforce
// household (Account) ID, used when a household has no lead to launch from:
// acquired firms' existing clients, and — once converted leads stop being kept
// — every client whose lead has since been deleted.
function getHouseholdIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id =
      params.get('householdId') || params.get('householdid') || params.get('hhid');
    return id ? id.trim() : '';
  } catch {
    return '';
  }
}

// The PQ identity (= the household's External_Key__c in Salesforce). When
// launched from Salesforce, the Lead ID is the identity; otherwise a ?pqid=
// (a previously app-generated UUID) is used. leadId wins if both appear.
// When neither is present (standalone use), the app generates a UUID.
function getPqIdentityFromUrl() {
  return getLeadIdFromUrl() || getPqidFromUrl();
}

// What the PQ was launched against, as an id plus the KIND of record it names.
// The kind is what the payload and the form both key off: a lead launch owns
// the whole form, a household launch may not write the household's own people
// back to Salesforce (householdOwned.js).
//
// A lead wins when both are present. The Salesforce button passes one or the
// other by design — lead if the household has one, household if it does not —
// but a hand-edited URL carrying both should behave like the lead launch it
// most resembles rather than silently withholding half the form.
function getPqLaunchFromUrl() {
  const leadOrPqid = getPqIdentityFromUrl();
  if (leadOrPqid) return { id: leadOrPqid, kind: 'lead' };
  const householdId = getHouseholdIdFromUrl();
  if (householdId) return { id: householdId, kind: 'household' };
  return { id: '', kind: '' };
}

// The identity the store's `_uid` takes from the URL: the Lead ID when
// launched from a lead, the household ID when launched from a household.
// Both are the key the record is looked up and upserted by, so both have to
// take part in the "did we switch households?" check below.
function getRecordIdFromUrl() {
  return getLeadIdFromUrl() || getHouseholdIdFromUrl();
}

// Read-only ("view") mode, from ?view=1 / ?readonly=1 in the URL. Planning
// opens the PQ from the Plan Delivery case to READ a household, and today the
// form autosaves under them, so they navigate it afraid to touch anything.
// That fear is well founded: the auto-source syncs mutate rows on load, which
// dirties the digest and pushes to Salesforce even when nobody typed.
//
// So this flag is enforced at the SYNC layer, not just by disabling inputs:
// it pins the Salesforce write gate closed and takes localStorage out of the
// loop entirely. Disabled inputs alone would still write on open.
function getReadOnlyFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    // An iPad joined to a meeting screen (?pair=) shows what the screen holds
    // and sends its drawing there to be saved. It writes nothing of its own.
    if ((params.get('pair') || '').trim()) return true;
    const raw = params.get('view') ?? params.get('readonly') ?? params.get('readOnly');
    if (raw == null) return false;
    // Bare ?view is truthy; ?view=0/false/no is not.
    const v = raw.trim().toLowerCase();
    return v === '' || !['0', 'false', 'no', 'off'].includes(v);
  } catch {
    return false;
  }
}

// Planning mode, from the /planning path itself (so the Salesforce button is
// one clean URL: /planning?leadId=...), or ?mode=planning / ?planning=1. The
// planning team's editable workspace: same store, same saves, but every change
// is recorded in the change log with who and when, measured from the advisor's
// handoff snapshot. Unlike view mode it WRITES — the log is what makes that
// safe.
// Held back from production: the change log this mode writes lives in the
// Salesforce field planningChangeLog, which does not exist there yet. Gating
// the ROUTE alone would not be enough, because ?mode=planning and ?planning=1
// reach this same function and it is planningMode, not the route, that turns
// the log on. Flip on once the field exists (Jimmy, 2026-08-19). Dev only
// since 2026-10-20, with the route in App.jsx (src/env.js).
const PLANNING_ENABLED = DEV_ONLY;

function getPlanningFromUrl() {
  if (!PLANNING_ENABLED) return false;
  try {
    if (/\/planning\/?$/i.test(window.location.pathname)) return true;
    const params = new URLSearchParams(window.location.search);
    if ((params.get('mode') || '').trim().toLowerCase() === 'planning') return true;
    const raw = params.get('planning');
    if (raw == null) return false;
    const v = raw.trim().toLowerCase();
    return v === '' || !['0', 'false', 'no', 'off'].includes(v);
  } catch {
    return false;
  }
}

// Sessions that must leave no trace in localStorage: view mode and planning
// mode. Both are how OTHER people open an advisor's household, and both hop
// between households — while STORE_KEY is one global slot whose normal path
// DELETES the cached draft when the URL names a different lead. Reading or
// writing it from these sessions would wipe work in progress and leave another
// household's PII on the device. Planning mode still syncs to Salesforce; it
// just holds everything in memory on the way.
function isEphemeralSession() {
  return getReadOnlyFromUrl() || getPlanningFromUrl();
}

// Read a ?caseId= value from the current URL, if present.
function getCaseIdFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('caseId') || params.get('caseid');
    return id ? id.trim() : '';
  } catch {
    return '';
  }
}

// Fetch an existing record from Salesforce by external key (Lead ID or a
// previously generated pqid UUID) and map it to store shape. Returns null
// when no record exists yet — the expected case for a brand-new Lead launch,
// which should start on a blank form rather than surface an error.
async function loadFromSalesforce(recordId, kind = 'lead') {
  // A household launch looks the Financial Profile up by the household it
  // hangs off rather than by a lead that does not exist.
  const param = kind === 'household' ? 'householdId' : 'leadId';
  const response = await fetch(
    `/api/FinancialProfileService?${param}=${encodeURIComponent(recordId)}`,
    { method: 'GET', headers: { Accept: 'application/json' } }
  );
  // No Financial Profile yet for this key — not an error, just a fresh start.
  if (response.status === 404) return null;
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Salesforce load failed (${response.status}): ${text}`);
  }
  if (!text) return null;
  let sf;
  try {
    sf = JSON.parse(text);
  } catch {
    return null;
  }
  // An empty object/array also means "no record yet".
  if (!sf || (typeof sf === 'object' && Object.keys(sf).length === 0)) return null;
  const mapped = fromSalesforce(sf);
  // Which save this is, so the page's next save can prove it saw it. Kept on
  // the store (an underscore key, so never part of the payload or its digest)
  // so it travels into localStorage with the data it describes.
  if (sf.pqVersion) mapped._pqVersion = sf.pqVersion;
  return mapped;
}

// Fetch an existing Plan Order from Salesforce by caseId and map it. Returns
// null when no Plan Order exists yet — the expected case for a first-time
// Plan Order Form launch (the Plan Delivery Case exists, but no Plan Order has
// been saved against it). Mirrors loadFromSalesforce so a cold launch starts
// blank instead of flashing the "couldn't load" banner.
async function loadPlanOrderFromSalesforce(caseId, leadId) {
  const params = new URLSearchParams({ caseId });
  if (leadId) params.append('leadId', leadId);
  const response = await fetch(
    `/api/PlanOrderFormService?${params}`,
    { method: 'GET', headers: { Accept: 'application/json' } }
  );
  // No Plan Order saved for this Case yet — not an error, just a fresh start.
  if (response.status === 404) return null;
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`Plan Order load failed (${response.status}): ${text}`);
  }
  if (!text) return null;
  let sf;
  try {
    sf = JSON.parse(text);
  } catch {
    return null;
  }
  // The GET response wraps the plan order fields under a top-level `data`
  // key, e.g. { data: { ...fields... } }. Unwrap it before mapping; fall back
  // to the raw object if a future response comes back already flat.
  const record = sf.data && typeof sf.data === 'object' ? sf.data : sf;
  // An empty object/array also means "no Plan Order yet".
  if (!record || (typeof record === 'object' && Object.keys(record).length === 0)) return null;
  return fromPlanOrder(record);
}

// Pull the Case ID out of whatever shape Salesforce returns it in. The
// Plan Order Form is linked to a Plan Delivery Case on first save; the
// response carries that Case's ID so subsequent saves can target it.
function extractCaseId(result) {
  if (!result || typeof result !== 'object') return '';
  const candidates = [
    result.caseId,
    result.caseID,
    result.CaseId,
    result.case_id,
    result.id,
    result.Id,
    result.recordId,
    result.data?.caseId,
    result.data?.id,
  ];
  const found = candidates.find(v => v != null && String(v).trim() !== '');
  return found ? String(found).trim() : '';
}

// Push the Plan Order to Salesforce. Salesforce upserts on caseId (the Plan
// Delivery Case), so first POST creates and subsequent POSTs update.
// Throws on a non-OK response so the caller can surface the failure.
async function savePlanOrderToSalesforce(data) {
  const payload = toPlanOrder(data);
  const response = await fetch('/api/PlanOrderFormService', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await response.text();
  let result;
  try {
    result = text ? JSON.parse(text) : {};
  } catch {
    result = { raw: text };
  }
  if (!response.ok) {
    console.error('Plan Order save failed:', result);
    const msg = result?.error || result?.[0]?.message || `HTTP ${response.status}`;
    const err = new Error(msg);
    err.status = response.status;
    throw err;
  }
  // Salesforce can return 200 with a logical failure flag — treat it as a
  // failure (matches saveToSalesforce), so a real failure can't masquerade as
  // a successful send.
  if (result && result.success === false) {
    console.error('Plan Order save failed (success:false):', result);
    const err = new Error(result.error || 'Plan Order save failed');
    err.status = response.status;
    throw err;
  }
  return result;
}

// Gate for the Salesforce push: the client's first AND last name must be
// filled. We don't auto-save (or let a manual save through) until both are
// present, so the household record is never created half-named.
function canSaveToSalesforce(data) {
  return missingNameMessage(data) === null;
}

// Which required name(s) are missing, as a ready-to-show message — or null when
// both are present. Drives the specific error on a manual Save attempt.
function missingNameMessage(data) {
  const fam = data?.family || {};
  const first = (fam.client1FirstName || '').trim();
  const last = (fam.client1LastName || '').trim();
  if (!first && !last) return SAVE_MSG_NAME_BOTH;
  if (!first) return SAVE_MSG_NAME_FIRST;
  if (!last) return SAVE_MSG_NAME_LAST;
  return null;
}

// Ensure the household has a stable root `_uid`. Used as the upsert key
// (PQId__c) by Salesforce's FinancialProfileService — first save creates,
// subsequent saves update the same record.
function ensureRootUid(data) {
  if (data && typeof data === 'object' && !data._uid) {
    return { ...data, _uid: genUid() };
  }
  return data;
}

// A row auto-seeded to keep a table non-empty carries only internal
// (_-prefixed) keys and no user-entered values.
function isBlankRow(row) {
  return (
    row && typeof row === 'object' &&
    Object.keys(row).every(k => k.startsWith('_'))
  );
}

// Does the persisted store already hold a real record? Either a Salesforce
// identity from a prior save, or user-entered client name content. Used to
// decide whether a bare launch (no URL reference) is truly a mis-launch.
function hasSavedRecord(d) {
  if (!d || typeof d !== 'object') return false;
  if (d._leadId || d._financialProfileId) return true;
  const fam = d.family || {};
  const filled = v => typeof v === 'string' && v.trim() !== '';
  return filled(fam.client1FirstName) || filled(fam.client1LastName);
}

export function StoreProvider({ children }) {
  // PQ identity from the URL: the Salesforce Lead ID (?leadId=) when launched
  // from Salesforce, or a ?pqid= UUID otherwise. Both resolve to the
  // household's External_Key__c, so the same GET-by-external-key loads either.
  // Failing both, the household ID (?householdId=) for a household that has no
  // lead — same store, same saves, minus the household's own fields.
  const launch = useMemo(getPqLaunchFromUrl, []);
  const pqIdentityFromUrl = launch.id;
  // Fixed for the life of the tab, like view and planning mode: it comes from
  // the URL and nothing in the app can turn it on or off.
  const householdMode = launch.kind === 'household';
  const caseIdFromUrl = getCaseIdFromUrl();
  // Read-only ("view") mode. Fixed for the life of the tab: it comes from the
  // URL and nothing in the app can turn it off.
  const readOnly = getReadOnlyFromUrl();
  // Planning mode. Also fixed for the life of the tab. Writes like normal mode
  // but records every change in the change log; holds nothing in localStorage.
  const planningMode = getPlanningFromUrl();
  // True when the app was opened with no client reference at all: no
  // leadId/pqid/caseId in the URL and nothing already saved in this browser.
  // In production the app is always launched from Salesforce with a leadId, so
  // this flags a mis-launch (e.g. landing on the bare URL) rather than silently
  // showing a blank form whose saves can't bind to a record. Computed once.
  const noReference = useMemo(
    () => !DEMO_MODE && !pqIdentityFromUrl && !caseIdFromUrl && !hasSavedRecord(readFromStorage()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [data, setData] = useState(readFromStorage);
  // Snapshot, taken once before any effect runs, of what the browser held at
  // page load and whether it was ever confirmed saved. The hydration effect
  // reads this to decide whether the Salesforce load may replace local data,
  // uncontaminated by the derived-field / auto-row effects that fire after
  // mount. `data` here is still the initial (localStorage) state.
  const loadBaselineRef = useRef(null);
  if (loadBaselineRef.current === null) {
    loadBaselineRef.current = {
      localDigest: pqDigest(data),
      localHasContent: hasSavedRecord(data),
      syncedDigest: readSyncedDigest(),
    };
  }
  // True when the hydration decided to keep unsynced local edits over the loaded
  // Salesforce record, so the UI can tell the advisor their old edits were
  // recovered (and will sync). Cleared on the next successful save.
  const [restoredLocalEdits, setRestoredLocalEdits] = useState(false);
  // 'idle' | 'saving' | 'saved' | 'error'
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  // Message for the most recent failed PQ save (null when the last save was OK).
  // Values Salesforce refused, held back so the REST of the record can still
  // save. Keyed by path, valued by the exact rejected value: when the advisor
  // edits that field the value differs, the hold is lifted, and it is tried
  // again on the next tick. Without keying on the value, a held-back field
  // would either be retried every 10s (re-running the search each time) or
  // never retried at all.
  const quarantineRef = useRef(new Map());
  const [quarantined, setQuarantined] = useState([]);
  const [saveError, setSaveError] = useState(null);
  // Whether the most recent save failure was a user-fixable validation problem
  // ('validation') or a system/network problem ('system'). null when OK.
  const [saveErrorKind, setSaveErrorKind] = useState(null);
  // When a PQ identity (leadId/pqid) or ?caseId= is in the URL, we start in a
  // loading state and hydrate from Salesforce before showing the form.
  const [loading, setLoading] = useState(!!pqIdentityFromUrl || !!caseIdFromUrl);
  const [loadError, setLoadError] = useState(null);
  // Cross-tab co-editing state. null when this tab owns the Salesforce write
  // lock (the normal case). 'secondary' when another tab holds it, so this tab
  // is read-only for sync (its edits stay in localStorage but aren't pushed).
  // 'conflict' is the no-Web-Locks fallback: a sibling tab confirmed a diverging
  // save, so we pause syncing and ask the advisor to reload. Drives a banner.
  const [coedit, setCoedit] = useState(null);
  // Gates every Salesforce push (autosave tick, close-time beacon, manual save).
  // A ref so the handlers read the live value without re-subscribing. Starts
  // true — the common case is a single tab, and the election below flips it to
  // false within a tick if another tab already owns the lock.
  //
  // In view mode it starts false and STAYS false: this is the single control
  // that makes read-only real. Every Salesforce push already funnels through
  // it (the autosave tick, the close-time beacon, and manual save), so pinning
  // it closed is what guarantees a viewer cannot write, no matter what the UI
  // or the auto-source syncs do to the in-memory store.
  const isWriterRef = useRef(!readOnly);

  // Which save of the record this page is working from (see pqVersion.js).
  // Seeded from what the browser held, so a page that reads its data from
  // localStorage without loading also knows which save that data came from.
  const versionsRef = useRef(emptyVersions(data._pqVersion));
  // Set once Salesforce refuses a save from this page as stale. From then on
  // the page does not save at all until the advisor loads the latest version.
  const staleRef = useRef(false);

  // Adopt a version Salesforce confirmed, and keep it beside the data in the
  // store (and so in localStorage) so the two never describe different saves.
  const adoptVersion = version => {
    if (!version) return;
    versionsRef.current = confirmVersion(versionsRef.current, version);
    setData(prev => (prev._pqVersion === version ? prev : { ...prev, _pqVersion: version }));
  };

  // Another page or device saved since this one last looked. Stop saving here
  // rather than write this page's copy over that work, and say so.
  const markStale = () => {
    if (staleRef.current) return;
    staleRef.current = true;
    isWriterRef.current = false;
    clearTimeout(flashTimer.current);
    setAutosaveStatus('idle');
    setCoedit('stale');
    trackEvent('pq_save_stale', {});
  };

  // Every save from this page goes through here, so each one carries the
  // versions the page could be working from and learns the one it produced.
  // A save that fails without a verdict may still have landed, so its version
  // is kept as one the page could be working from too.
  const postVersioned = async payload => {
    const body = JSON.stringify(payload);
    const sent = await versionOfBody(body);
    const res = await postPayload(payload, versionsRef.current);
    if (res.conflict) markStale();
    else if (res.ok) adoptVersion(res.version || sent);
    else versionsRef.current = addUnconfirmed(versionsRef.current, sent);
    return res;
  };

  // --- Planning change log machinery ---
  // Who the planner is, resolved once from /.auth/me. A ref so the save loops
  // read the live value; '' until the fetch lands (or forever, in dev).
  const plannerIdRef = useRef('');
  useEffect(() => {
    if (planningMode) getUserIdentity().then(v => { plannerIdRef.current = v; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The form slice at the last successful save — the baseline the next save's
  // changes are measured against. Seeded when the Salesforce record is adopted
  // at hydration, so mount-time machine churn (seeded rows, healed prefixes)
  // is part of the baseline and never reads as a planner's edit.
  const lastLoggedDataRef = useRef(null);

  // Fold this save's changes into the change log so the entries ride the SAME
  // POST as the changes they describe — a log that arrives one save behind
  // its changes would leave the record momentarily claiming edits nobody made
  // (or the reverse) on every crash between the two.
  // Returns { toSave, sliceAfter }: the (possibly log-carrying) object to POST
  // and the form slice to adopt as the next baseline on success.
  const foldPlanningLog = current => {
    if (!planningMode) return { toSave: current, sliceAfter: null };
    const slice = pqFormSlice(current);
    const baseline = lastLoggedDataRef.current;
    if (!baseline) return { toSave: current, sliceAfter: slice };
    const entries = diffPq(baseline, slice);
    if (entries.length === 0) return { toSave: current, sliceAfter: slice };
    return {
      toSave: {
        ...current,
        planningLog: appendLogEntries(
          current.planningLog,
          entries,
          plannerIdRef.current || 'Planning team',
          new Date().toISOString()
        ),
      },
      sliceAfter: slice,
    };
  };

  const flashTimer = useRef(null);
  // Latest PQ digest pushed to Salesforce. The interval below compares each
  // tick's digest against this; only if they differ do we POST.
  const lastSyncedDigestRef = useRef(pqDigest(data));
  // Latest PQ digest handed to a close-time beacon (see the exit-flush effect).
  // Kept separate from lastSyncedDigestRef so a beacon — which we can't confirm
  // the server accepted — never suppresses the real autosave loop's retry; it
  // only prevents the same teardown from firing duplicate beacons.
  const lastBeaconDigestRef = useRef('');
  // True while a POST to Salesforce is awaiting a response. The autosave tick
  // skips while set, so two saves can never overlap (an edit made during a
  // slow save used to let the next tick fire a second, dueling POST).
  const saveInFlightRef = useRef(false);
  // Consecutive autosave failures + the earliest time the next retry may run
  // (drives the exponential backoff; both reset on any success or manual save).
  const failureCountRef = useRef(0);
  const nextRetryAtRef = useRef(0);
  // Mirror the current `data` so the interval handler can read it without
  // closing over a stale value (and without rerunning the interval on
  // every keystroke).
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Mirror saveErrorKind so the dirty effect can read it WITHOUT depending on
  // it. If it were a dependency, setting a required-name error would re-run the
  // effect immediately and (because the form is already dirty) clear the error
  // in the same tick — it would only flash for a moment.
  const saveErrorKindRef = useRef(saveErrorKind);
  useEffect(() => {
    saveErrorKindRef.current = saveErrorKind;
  }, [saveErrorKind]);

  // On mount: hydrate from Salesforce based on URL params.
  // - leadId / pqid → PQ form record (FinancialProfileService), looked up by
  //   External_Key__c. A leadId-launch for a brand-new Lead returns nothing
  //   yet — the form starts blank and the first save creates the record with
  //   External_Key__c = leadId.
  // - caseId → Plan Order Form record (PlanOrderFormService)
  // - If both are present, load PQ first then merge Plan Order on top.
  useEffect(() => {
    if (!pqIdentityFromUrl && !caseIdFromUrl) return;
    let cancelled = false;
    (async () => {
      try {
        let sfPq = null;
        let planLoaded = null;
        if (pqIdentityFromUrl) {
          sfPq = await loadFromSalesforce(pqIdentityFromUrl, launch.kind);
        }
        if (caseIdFromUrl) {
          planLoaded = await loadPlanOrderFromSalesforce(caseIdFromUrl, pqIdentityFromUrl);
        }
        if (cancelled) return;
        const sfData = sfPq || {};
        // Decide whether the loaded record may replace what the browser holds,
        // or whether the browser has unsynced edits that must be preserved
        // (a save that failed during an outage, a dropped close-time beacon).
        const baseline = loadBaselineRef.current;
        const decision = resolveHydration({
          localDigest: baseline.localDigest,
          sfDigest: pqDigest(sfData),
          syncedDigest: baseline.syncedDigest,
          localHasContent: baseline.localHasContent,
        });

        if (decision === 'local') {
          // Keep the browser's unsynced PQ edits. Overlay any freshly loaded
          // Plan Order (POF isn't part of PQ dirtiness) and force the
          // authoritative URL identifiers onto the preserved state.
          setData(prev => {
            const next = { ...prev, ...(planLoaded || {}) };
            if (pqIdentityFromUrl) next._uid = pqIdentityFromUrl;
            if (householdMode) next._householdId = pqIdentityFromUrl;
            if (caseIdFromUrl) next._caseId = caseIdFromUrl;
            return next;
          });
          // Mark the PQ as unsynced against what Salesforce currently has, so
          // the autosave loop pushes the preserved local edits up. Pushing them
          // over the record is the decision just made, so the page works from
          // the version it just loaded rather than whatever the browser held.
          lastSyncedDigestRef.current = pqDigest(sfData);
          adoptVersion(sfData._pqVersion);
          setRestoredLocalEdits(true);
          trackEvent('pq_hydrate_kept_local', {});
        } else {
          // Adopt the Salesforce record: no local edits at stake, or local
          // already matches it.
          //
          // The assessment slice is carried across rather than adopted. It is
          // not part of PQ_SLICE_KEYS, so it never goes up to Salesforce and
          // never comes back down, which means spreading sfData over the top
          // silently DELETES it and the following writeToStorage makes that
          // permanent. That cost an advisor their whiteboard drawing the
          // moment they reopened the household. Everything in here is derived
          // or drawn locally (analyzed holdings, sketches), so local is the
          // only copy there is and Salesforce has no opinion to overrule it
          // with. Until the Financial Profile carries a JSON field for it,
          // this line is what makes any of it survive a reopen.
          const localAssessment = dataRef.current?.assessment;
          const merged = { ...sfData, ...(planLoaded || {}) };
          if (localAssessment) merged.assessment = localAssessment;
          // The URL PQ identity (leadId or pqid) is authoritative — it IS the
          // household's external key. Force it onto the store so the first
          // save upserts on the right key (e.g. External_Key__c = leadId),
          // regardless of what the load returned (empty for a new Lead).
          if (pqIdentityFromUrl) {
            merged._uid = pqIdentityFromUrl;
          }
          // What kind of record that identity names travels with it. Without
          // this the payload would fall back to sending the household ID as a
          // leadId, which Salesforce refuses outright.
          if (householdMode) {
            merged._householdId = pqIdentityFromUrl;
          }
          // Likewise the caseId from the Salesforce email link is authoritative
          // for the pre-existing Plan Delivery Case — echo it on every POST.
          if (caseIdFromUrl) {
            merged._caseId = caseIdFromUrl;
          }
          versionsRef.current = emptyVersions(merged._pqVersion);
          setData(merged);
          writeToStorage(merged);
          // We just loaded this data from Salesforce — mark it as already
          // synced so the interval tick doesn't immediately POST it back, and
          // persist that marker so a later reload knows this state was synced.
          const mergedDigest = pqDigest(merged);
          lastSyncedDigestRef.current = mergedDigest;
          writeSyncedDigest(mergedDigest);
          // The change-log baseline starts at what Salesforce holds, so
          // mount-time machine churn never reads as a planner's edit.
          lastLoggedDataRef.current = pqFormSlice(merged);
        }
      } catch (err) {
        console.error('Failed to load record from Salesforce:', err);
        // Even if the load fails, still capture the URL identity + caseId so
        // a save creates/links the right records.
        if (!cancelled && (pqIdentityFromUrl || caseIdFromUrl)) {
          setData(prev => {
            const next = {
              ...prev,
              ...(pqIdentityFromUrl ? { _uid: pqIdentityFromUrl } : {}),
              ...(householdMode ? { _householdId: pqIdentityFromUrl } : {}),
              ...(caseIdFromUrl ? { _caseId: caseIdFromUrl } : {}),
            };
            writeToStorage(next);
            return next;
          });
        }
        if (!cancelled) setLoadError(err.message || 'Failed to load record');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write to localStorage on every data change (no delay — fast + safe).
  useEffect(() => {
    writeToStorage(data);
  }, [data]);

  // Surface pending unsynced PQ edits in the autosave pill: "saving" while
  // the local PQ digest differs from the last synced one. Doesn't fire for
  // Plan Order edits since planOrder is excluded from pqDigest.
  useEffect(() => {
    if (DEMO_MODE) return; // no sync pill in the standalone demo
    const dirty = pqDigest(data) !== lastSyncedDigestRef.current;
    if (!dirty) return;
    const canSave = canSaveToSalesforce(data);
    // A required-name error is transient — clear it as soon as the user edits
    // anything, then reflect whether a push is now possible. Read the kind from
    // a ref so this effect only runs on real data edits, not when the error is
    // first set (which would clear it instantly).
    if (saveErrorKindRef.current === 'required') {
      setSaveError(null);
      setSaveErrorKind(null);
      setAutosaveStatus(canSave ? 'saving' : 'blocked');
      return;
    }
    // Don't downgrade a real save failure to "saving" on a mere edit. Without
    // the required name nothing can sync, so show 'blocked' (an amber "name
    // required" pill) instead of idling — otherwise the advisor types away
    // with no hint that none of it is reaching Salesforce yet.
    setAutosaveStatus(s => (s === 'error' ? s : canSave ? 'saving' : 'blocked'));
  }, [data]);

  // Periodic Salesforce sync. Every AUTOSAVE_TICK_MS, compare the current
  // PQ digest to the last successfully synced one; only POST if changed.
  // The last payload Salesforce ACCEPTED. lastSyncedDigestRef holds it as JSON
  // already (pqDigest is a stringified toSalesforce payload), so it doubles as
  // the known-good baseline the quarantine search needs.
  const parseLastGood = () => {
    try {
      const raw = lastSyncedDigestRef.current;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // Changes worth sending: everything that differs from the last accepted
  // payload, minus any held-back value the advisor has not touched since.
  const sendableChanges = (current, lastGood) => {
    const all = changedPaths(lastGood, current);
    if (quarantineRef.current.size === 0) return all;
    return all.filter(path => {
      const rejected = quarantineRef.current.get(pathKey(path));
      if (rejected === undefined) return true;
      return JSON.stringify(getAtPath(current, path) ?? null) !== rejected;
    });
  };

  const describeQuarantine = () =>
    [...quarantineRef.current.keys()].map(key => ({
      key,
      label: labelForPath(key.split('.').map(seg => (/^\d+$/.test(seg) ? Number(seg) : seg)), labelForKey),
    }));

  useEffect(() => {
    if (DEMO_MODE) return; // no Salesforce backend in the standalone demo
    const tick = async () => {
      // Only the tab that holds the cross-tab writer lock syncs to Salesforce.
      if (!isWriterRef.current) return;
      // Never overlap saves, and honor the failure backoff window.
      if (saveInFlightRef.current) return;
      if (Date.now() < nextRetryAtRef.current) return;
      const original = dataRef.current;
      if (pqDigest(original) === lastSyncedDigestRef.current) return;
      if (!canSaveToSalesforce(original)) return;

      // Planning mode: fold this save's changes into the change log so the
      // entries ride the same POST as the changes they describe. The fold
      // cannot make a clean state dirty — entries exist only when the form
      // itself changed, which the check above already established.
      const { toSave, sliceAfter } = foldPlanningLog(original);
      const currentPayload = toSalesforce(toSave);
      const currentDigest = JSON.stringify(currentPayload);
      const lastGood = parseLastGood();

      // Quarantine applies to the ordinary advisor flow only. Planning folds a
      // change log into the same POST, and a partial save would leave the log
      // describing changes that did not land. Planning is staging-only today.
      const canQuarantine = !planningMode && !!lastGood;

      let sendable = null;
      let payloadToSend = currentPayload;
      if (canQuarantine) {
        sendable = sendableChanges(currentPayload, lastGood);
        // Everything sendable is already saved; only held-back values remain.
        // Returning keeps the banner up without re-running the search forever.
        if (sendable.length === 0) return;
        payloadToSend = buildCandidate(lastGood, currentPayload, sendable);
      }

      setAutosaveStatus('saving');
      saveInFlightRef.current = true;
      const started = Date.now();
      let res;
      try {
        res = await postVersioned(payloadToSend);

        // A rejection here is not the end of the attempt. Find which of the
        // advisor's changes Salesforce will not take, then save the rest, so
        // one bad value costs one field instead of the whole session.
        if (!res.ok && !res.conflict && canQuarantine && classifySaveError(res.status, res.rejectedByServer) === 'validation') {
          const { bad, good, probes } = await isolateBadPaths(sendable, paths =>
            postVersioned(buildCandidate(lastGood, currentPayload, paths)).then(r => r.ok)
          );
          trackEvent('pq_save_quarantine', {
            probes, bad: bad.length, good: good.length, changed: sendable.length,
          });
          if (bad.length === 0) {
            // The full set passed on retry, so the first failure was transient.
            res = await postVersioned(buildCandidate(lastGood, currentPayload, sendable));
          } else {
            for (const path of bad) {
              quarantineRef.current.set(
                pathKey(path),
                JSON.stringify(getAtPath(currentPayload, path) ?? null)
              );
            }
            // Re-send the accepted subset so Salesforce ends on a known state
            // rather than on whatever the last probe happened to write.
            const goodPayload = buildCandidate(lastGood, currentPayload, good);
            const goodRes = await postVersioned(goodPayload);
            if (goodRes.ok) {
              payloadToSend = goodPayload;
              res = { ...goodRes, quarantinedOnly: true };
            }
          }
        }
      } finally {
        saveInFlightRef.current = false;
      }
      // Refused as stale (here or partway through the search): markStale has
      // already stopped the page and put up the banner. Nothing to retry.
      if (staleRef.current) return;
      const savedDigestForTick = JSON.stringify(payloadToSend);
      if (res.ok) {
        applyPqIds(setData, res.result);
        if (toSave !== original) {
          // Adopt the appended log without clobbering typing that happened
          // while the POST was on the wire.
          setData(prev => ({ ...prev, planningLog: toSave.planningLog }));
        }
        if (sliceAfter) lastLoggedDataRef.current = sliceAfter;
        // The digest of what Salesforce actually took, which is not the same as
        // the current form when a value is being held back.
        lastSyncedDigestRef.current = savedDigestForTick;
        writeSyncedDigest(savedDigestForTick);
        failureCountRef.current = 0;
        nextRetryAtRef.current = 0;
        setRestoredLocalEdits(false);
        if (res.quarantinedOnly) {
          // The rest of the record is safely saved. Keep the banner up, now
          // naming the field rather than asking the advisor to hunt for it.
          const held = describeQuarantine();
          setQuarantined(held);
          setSaveError(null);
          setSaveErrorKind('quarantined');
          setAutosaveStatus('error');
        } else {
          quarantineRef.current.clear();
          setQuarantined([]);
          setSaveError(null);
          setSaveErrorKind(null);
          setAutosaveStatus('saved');
          trackEvent('pq_autosave_ok', { durationMs: Date.now() - started });
          clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(
            () => setAutosaveStatus('idle'),
            SAVED_FLASH_MS
          );
        }
      } else {
        // Leave lastSyncedDigestRef unchanged so a later tick retries, but
        // back off exponentially so a persistent failure doesn't hammer
        // Salesforce every 10s. Cancel any pending saved→idle flash so it
        // can't clear this error.
        failureCountRef.current += 1;
        nextRetryAtRef.current = Date.now() + backoffMs(failureCountRef.current);
        clearTimeout(flashTimer.current);
        setSaveError(res.error);
        setSaveErrorKind(classifySaveError(res.status, res.rejectedByServer));
        setAutosaveStatus('error');
        trackEvent('pq_autosave_failed', {
          status: String(res.status ?? 'network'),
          kind: classifySaveError(res.status, res.rejectedByServer),
          attempt: failureCountRef.current,
          durationMs: Date.now() - started,
        });
      }
    };
    const id = setInterval(tick, AUTOSAVE_TICK_MS);
    return () => {
      clearInterval(id);
      clearTimeout(flashTimer.current);
    };
  }, []);

  // Close-time flush. The 10s autosave interval leaves a window where the most
  // recent edits live only in localStorage; if the advisor closes the tab (or
  // the record is next opened on another device) before the next tick, those
  // edits never reach Salesforce. A normal fetch can't finish once the page is
  // tearing down, so on exit we use navigator.sendBeacon (with a keepalive
  // fetch fallback) — both are delivered by the browser even after the page is
  // gone. Same guards as the interval: only fire on genuinely unsynced edits
  // with the required name present, so we never create a half-named record.
  useEffect(() => {
    if (DEMO_MODE) return; // no Salesforce backend in the standalone demo

    const flush = () => {
      if (!isWriterRef.current) return; // a sibling tab owns the write lock
      if (saveInFlightRef.current) return; // a normal save is already on the wire
      const current = dataRef.current;
      const currentDigest = pqDigest(current);
      if (currentDigest === lastSyncedDigestRef.current) return; // nothing unsynced
      if (currentDigest === lastBeaconDigestRef.current) return; // already beaconed this state
      if (!canSaveToSalesforce(current)) return; // name gate, matches the loop

      let body;
      try {
        // Planning mode: the final flush carries the log for its changes too.
        // Refs are left alone — a beacon is never confirmed, so the autosave
        // loop must stay free to retry the same state.
        body = JSON.stringify(toSalesforce(foldPlanningLog(current).toSave));
      } catch {
        return;
      }
      // The flush carries the page's versions like any other save, so a stale
      // page closing is refused too; it is the moment a forgotten tab is most
      // likely to fire.
      const url = saveUrl(versionsRef.current);
      let sent = false;
      try {
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          // A typed Blob makes the browser send Content-Type: application/json,
          // so the endpoint parses it exactly like a normal POST. Same origin,
          // so no CORS preflight (which sendBeacon can't do) is triggered.
          const blob = new Blob([body], { type: 'application/json' });
          sent = navigator.sendBeacon(url, blob);
        }
      } catch {
        sent = false;
      }
      if (!sent) {
        // sendBeacon missing or refused (e.g. queue full / payload too large) —
        // a keepalive fetch also survives unload. Best effort; ignore failures.
        try {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        } catch {
          return;
        }
      }
      // Remember what we beaconed so a follow-on hide/pagehide in the same
      // teardown doesn't fire a duplicate. We deliberately do NOT touch
      // lastSyncedDigestRef: if the tab actually stays alive (a mere tab
      // switch) and the beacon was dropped server-side, the normal autosave
      // loop must still be free to retry.
      lastBeaconDigestRef.current = currentDigest;
      // We never hear whether the flush landed, so if the tab lives on (a tab
      // switch, not a close) its save is one the page may now be working from.
      versionOfBody(body).then(v => {
        versionsRef.current = addUnconfirmed(versionsRef.current, v);
      });
      trackEvent('pq_exit_flush', { via: sent ? 'beacon' : 'keepalive' });
    };

    // visibilitychange→hidden is the most reliable "page may be going away"
    // signal (covers tab close, navigation, and mobile backgrounding); pagehide
    // is the belt-and-suspenders for browsers that skip it on hard unload.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Elect a single Salesforce writer for this household so two tabs on the same
  // lead can't silently overwrite each other. The first tab to claim the Web
  // Lock is the writer; any other tab goes read-only for sync (banner + gated
  // saves). When the writer's tab closes, the browser releases the lock and a
  // waiting tab is promoted — after reconciling against Salesforce so it doesn't
  // push a stale baseline over what the previous writer saved.
  useEffect(() => {
    if (DEMO_MODE) return; // no Salesforce backend in the standalone demo
    // A viewer must never contend for the write lock. Claiming it would demote
    // the advisor who actually has the form open to 'secondary' and stop THEIR
    // autosave — a read-only tab breaking an editing one.
    if (readOnly) return;
    if (!pqIdentityFromUrl) return; // no shared record to contend for
    if (typeof navigator === 'undefined' || !navigator.locks) return; // fallback below
    if (writerElectionRan) return; // run once per tab (StrictMode double-invoke guard)
    writerElectionRan = true;

    const lockName = writerLockName(pqIdentityFromUrl);
    const becomeWriter = () => {
      isWriterRef.current = true;
      setCoedit(null);
    };
    const becomeSecondary = () => {
      isWriterRef.current = false;
      setCoedit('secondary');
    };

    // Before a promoted tab starts writing, re-read Salesforce and run the SAME
    // resolver used at mount, so the former writer's final save isn't clobbered.
    // Best-effort: on any failure just take over (only one tab now, so
    // last-write-wins is safe again).
    const reconcileThenWrite = async () => {
      try {
        const sf = (await loadFromSalesforce(pqIdentityFromUrl, launch.kind)) || {};
        const current = dataRef.current;
        const sfDig = pqDigest(sf);
        const decision = resolveHydration({
          localDigest: pqDigest(current),
          sfDigest: sfDig,
          syncedDigest: readSyncedDigest(),
          localHasContent: hasSavedRecord(current),
        });
        if (decision === 'sf') {
          const merged = { ...sf, _uid: pqIdentityFromUrl };
          if (householdMode) merged._householdId = pqIdentityFromUrl;
          if (current._caseId) merged._caseId = current._caseId;
          versionsRef.current = emptyVersions(merged._pqVersion);
          setData(merged);
          writeToStorage(merged);
          const d = pqDigest(merged);
          lastSyncedDigestRef.current = d;
          writeSyncedDigest(d);
          lastLoggedDataRef.current = pqFormSlice(merged);
        } else {
          // Keep this tab's edits; mark them unsynced against Salesforce so the
          // autosave loop pushes them now that we're the writer.
          lastSyncedDigestRef.current = sfDig;
          adoptVersion(sf._pqVersion);
          setRestoredLocalEdits(true);
        }
      } catch {
        /* couldn't reload — proceed as writer anyway */
      }
    };

    (async () => {
      // Grab the lock immediately if it's free → we're the primary writer.
      const acquired = new Promise(resolve => {
        navigator.locks
          .request(lockName, { ifAvailable: true }, lock => {
            if (!lock) {
              resolve(false); // busy: another tab is the writer
              return;
            }
            resolve(true);
            becomeWriter();
            return new Promise(() => {}); // hold until the browser frees it on tab close
          })
          .catch(() => resolve(false));
      });
      if (await acquired) return;

      // Another tab owns the lock → read-only here, then queue a blocking
      // request so we're promoted when the current writer releases (closes).
      becomeSecondary();
      trackEvent('pq_tab_secondary', {});
      try {
        await navigator.locks.request(lockName, async () => {
          await reconcileThenWrite();
          becomeWriter();
          trackEvent('pq_tab_promoted', {});
          await new Promise(() => {}); // hold for the rest of this tab's life
        });
      } catch {
        /* request rejected — nothing to do */
      }
    })();
    // No cleanup: the browser auto-releases the Web Lock on tab unload, and the
    // provider lives for the tab's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback for browsers without the Web Locks API: can't elect a single
  // writer, so watch the shared "last synced" marker instead. When another tab
  // confirms a save whose data diverges from ours, we have a genuine cross-tab
  // conflict — pause syncing and surface a banner so edits aren't silently
  // overwritten. The advisor reloads to continue from the latest record.
  useEffect(() => {
    if (DEMO_MODE) return;
    if (readOnly) return; // a viewer never syncs, so it can't conflict
    if (typeof navigator !== 'undefined' && navigator.locks) return; // Web Locks path handles it
    const onStorage = e => {
      if (e.key !== SYNCED_DIGEST_KEY || e.newValue == null) return;
      const sibling = e.newValue;
      if (pqDigest(dataRef.current) === sibling) {
        // Coincidentally identical — adopt the marker, no conflict.
        lastSyncedDigestRef.current = sibling;
        return;
      }
      isWriterRef.current = false;
      setCoedit('conflict');
      trackEvent('pq_tab_conflict', {});
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((path, value) => {
    setData(prev => setByPath(prev, path, value));
  }, []);

  const replace = useCallback(newData => {
    setData(newData ?? {});
  }, []);

  // --- Row-delete undo (R4) ---
  // The row × deletes instantly and autosave pushes it to Salesforce, so a
  // mis-click during a live call could silently drop typed numbers. Keep the
  // fast delete, but stash the removed row for a few seconds so an Undo (toast
  // click or Ctrl/Cmd+Z) can splice it back at its original index, _uid intact.
  const [pendingUndo, setPendingUndo] = useState(null);
  const pendingUndoRef = useRef(null);
  const undoTimerRef = useRef(null);
  const setPending = useCallback(v => {
    pendingUndoRef.current = v;
    setPendingUndo(v);
  }, []);

  const deleteRow = useCallback((path, index, row, opts = {}) => {
    setData(prev => {
      const arr = getByPath(prev, path);
      if (!Array.isArray(arr)) return prev;
      const next = arr.filter((_, i) => i !== index);
      // Preserve DataTable's reseed: a seeding table that goes empty gets one
      // blank row back so it never renders header-only.
      const finalArr = opts.noSeed ? next : (next.length ? next : [{ _uid: genUid() }]);
      return setByPath(prev, path, finalArr);
    });
    setPending({ path, index, row, noSeed: !!opts.noSeed, label: opts.label || null });
    clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setPending(null), UNDO_WINDOW_MS);
  }, [setPending]);

  const undoDelete = useCallback(() => {
    const pu = pendingUndoRef.current;
    if (!pu) return;
    clearTimeout(undoTimerRef.current);
    setData(prev => {
      const arr = getByPath(prev, pu.path);
      const base = Array.isArray(arr) ? arr : [];
      // If the delete emptied a seeding table, a lone blank auto-seed row was
      // inserted; drop it before restoring so no stray blank is left behind.
      const working = !pu.noSeed && base.length === 1 && isBlankRow(base[0]) ? [] : base;
      const restored = [...working];
      const at = Math.max(0, Math.min(pu.index, restored.length));
      restored.splice(at, 0, pu.row);
      return setByPath(prev, pu.path, restored);
    });
    setPending(null);
  }, [setPending]);

  // Clear the pending undo timer on unmount.
  useEffect(() => () => clearTimeout(undoTimerRef.current), []);

  const reset = useCallback(() => {
    clearTimeout(flashTimer.current);
    clearTimeout(undoTimerRef.current);
    setPending(null);
    localStorage.removeItem(STORE_KEY);
    writeSyncedDigest('');
    setData({});
    versionsRef.current = emptyVersions();
    lastSyncedDigestRef.current = '';
    failureCountRef.current = 0;
    nextRetryAtRef.current = 0;
    setSaveError(null);
    setSaveErrorKind(null);
    setRestoredLocalEdits(false);
    setAutosaveStatus('idle');
  }, []);

  // The way out of a page Salesforce refused as stale: load the record as it
  // is saved now and carry on from there. This page's unsaved copy of the PQ is
  // dropped on purpose, because it is older than what is saved. Anything that
  // is not part of the PQ (the Plan Order, the assessment) is kept.
  const loadLatest = useCallback(async () => {
    const current = dataRef.current;
    const household = householdMode || (!pqIdentityFromUrl && !!current._householdId);
    const identity = pqIdentityFromUrl || current._householdId || current._uid;
    try {
      const sf = (await loadFromSalesforce(identity, household ? 'household' : 'lead')) || {};
      const merged = { ...current };
      for (const key of PQ_SLICE_KEYS) delete merged[key];
      Object.assign(merged, sf);
      // The page keeps the identity it was opened with, as the first load
      // does. A lead's record comes back without its own id (financialProfile
      // .uid is empty), so the mapped copy names a generated placeholder, and
      // every save after "Load the latest version" went to Salesforce under
      // that placeholder and was refused (live test against james, 2026-11-02).
      merged._uid = pqIdentityFromUrl || current._uid || merged._uid;
      if (householdMode) merged._householdId = pqIdentityFromUrl;
      if (caseIdFromUrl) merged._caseId = caseIdFromUrl;
      if (sf._pqVersion) merged._pqVersion = sf._pqVersion;
      else delete merged._pqVersion;
      versionsRef.current = emptyVersions(sf._pqVersion);
      setData(merged);
      writeToStorage(merged);
      const digest = pqDigest(merged);
      lastSyncedDigestRef.current = digest;
      writeSyncedDigest(digest);
      lastLoggedDataRef.current = pqFormSlice(merged);
      lastBeaconDigestRef.current = '';
      failureCountRef.current = 0;
      nextRetryAtRef.current = 0;
      quarantineRef.current.clear();
      setQuarantined([]);
      setSaveError(null);
      setSaveErrorKind(null);
      setRestoredLocalEdits(false);
      staleRef.current = false;
      isWriterRef.current = !readOnly;
      setCoedit(null);
      trackEvent('pq_stale_reloaded', {});
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || 'Could not load the latest version' };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual Save button: forces a PQ push immediately (bypassing the 10s
  // interval). Always writes to localStorage; only pushes to Salesforce
  // when first + last name are filled.
  // Returns { ok } — on failure also { reason: 'read-only' | 'in-flight' |
  // 'stale' | 'validation' | 'system', error? } so callers that must not proceed on a
  // silent no-op (the Plan Order pre-send push) can tell the cases apart.
  const saveNow = useCallback(async () => {
    writeToStorage(data);
    if (DEMO_MODE) {
      // Demo: persist locally only and show a brief confirmation — no SF.
      setAutosaveStatus('saved');
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setAutosaveStatus('idle'), SAVED_FLASH_MS);
      return { ok: true };
    }
    if (!isWriterRef.current) {
      // Read-only tab: another tab owns the Salesforce write lock. Keep the edit
      // in localStorage (done above) but don't push — the co-edit banner already
      // explains why this tab isn't saving.
      return { ok: false, reason: 'read-only' };
    }
    const nameError = missingNameMessage(data);
    if (nameError) {
      // Manual save with a required name missing: surface a specific error so
      // the advisor knows exactly what to fix, rather than saving silently.
      // Entries are still safe in localStorage (written above).
      clearTimeout(flashTimer.current);
      setSaveError(nameError);
      setSaveErrorKind('required');
      setAutosaveStatus('error');
      return { ok: false, reason: 'validation', error: nameError };
    }
    // A manual save is also the user's "retry now" — clear any failure
    // backoff so the autosave loop resumes immediately afterwards.
    nextRetryAtRef.current = 0;
    if (saveInFlightRef.current) {
      // A save is already on the wire; don't fire a dueling POST. Whatever
      // this click was meant to include that the in-flight save missed still
      // differs from the synced digest, so the next tick sends it.
      setAutosaveStatus('saving');
      return { ok: false, reason: 'in-flight' };
    }
    setAutosaveStatus('saving');
    saveInFlightRef.current = true;
    const started = Date.now();
    // Same fold as the autosave tick: in planning mode the change log rides
    // the same POST as the changes it describes.
    const { toSave, sliceAfter } = foldPlanningLog(data);
    // Respect anything the autosave loop has already established that
    // Salesforce will not take. Without this, clicking Save re-sends the known
    // bad value, fails as a whole, and replaces the banner naming the field
    // with the older one that names nothing.
    const manualPayload = toSalesforce(toSave);
    const manualLastGood = parseLastGood();
    let manualToSend = manualPayload;
    if (!planningMode && manualLastGood && quarantineRef.current.size > 0) {
      const sendable = sendableChanges(manualPayload, manualLastGood);
      manualToSend = buildCandidate(manualLastGood, manualPayload, sendable);
    }
    let res;
    try {
      res = await postVersioned(manualToSend);
    } finally {
      saveInFlightRef.current = false;
    }
    if (res.conflict) {
      // The banner explains it; this is not an error in what was typed.
      return { ok: false, reason: 'stale', error: res.error };
    }
    if (res.ok) {
      applyPqIds(setData, res.result);
      if (toSave !== data) {
        setData(prev => ({ ...prev, planningLog: toSave.planningLog }));
      }
      if (sliceAfter) lastLoggedDataRef.current = sliceAfter;
      const savedDigest = JSON.stringify(manualToSend);
      lastSyncedDigestRef.current = savedDigest;
      writeSyncedDigest(savedDigest);
      failureCountRef.current = 0;
      setSaveError(null);
      setSaveErrorKind(null);
      setRestoredLocalEdits(false);
      setAutosaveStatus('saved');
      trackEvent('pq_manual_save_ok', { durationMs: Date.now() - started });
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(
        () => setAutosaveStatus('idle'),
        SAVED_FLASH_MS
      );
      return { ok: true };
    } else {
      failureCountRef.current += 1;
      nextRetryAtRef.current = Date.now() + backoffMs(failureCountRef.current);
      clearTimeout(flashTimer.current);
      setSaveError(res.error);
      setSaveErrorKind(classifySaveError(res.status, res.rejectedByServer));
      setAutosaveStatus('error');
      trackEvent('pq_manual_save_failed', {
        status: String(res.status ?? 'network'),
        kind: classifySaveError(res.status, res.rejectedByServer),
        durationMs: Date.now() - started,
      });
      return { ok: false, reason: classifySaveError(res.status, res.rejectedByServer), error: res.error };
    }
  }, [data]);

  const stripKeysMatching = useCallback(predicate => {
    setData(prev => stripDeep(prev, predicate));
  }, []);

  // Freeze the PQ as it stands into the handoff snapshot (data.pqArchive).
  // Called on a successful Plan Order Form SUBMIT — the workflow's "advisor is
  // done" moment — never by any load-time or render-time path, so a snapshot
  // always marks a person's deliberate action. The snapshot is the baseline
  // the planning change log is measured against, and the archived copy that
  // "View as handed off" renders. It rides the normal autosave to Salesforce
  // as one Long Text Area (field pending with Srivani).
  const archivePq = useCallback(async () => {
    const by = await getUserIdentity();
    setData(prev => ({
      ...prev,
      pqArchive: {
        at: new Date().toISOString(),
        by,
        data: pqFormSlice(prev),
      },
    }));
  }, []);

  // Explicit Plan Order push — triggered by the "Send to Planning" button
  // in Plan Mode. NOT called by the autosave loop.
  // Returns { ok: boolean, caseId?: string, error?: string }.
  const sendPlanOrder = useCallback(async () => {
    if (DEMO_MODE) {
      return { ok: false, error: 'Sending to Planning is disabled in the demo environment.' };
    }
    try {
      const result = await savePlanOrderToSalesforce(data);
      const caseId = extractCaseId(result);
      const planOrderFormId = result?.planOrderFormId
        ? String(result.planOrderFormId)
        : '';

      // Persist the returned ids so subsequent sends update the same record.
      // Dedup is driven by caseId (Salesforce upserts the Plan Order on its
      // Plan Delivery Case); caseId / planOrderFormId are stored for the
      // load-by-id flow + debugging. Write straight to localStorage too, so a
      // refresh keeps the linkage even before React state flushes.
      const next = {
        ...data,
        ...(caseId ? { _caseId: caseId } : {}),
        ...(planOrderFormId ? { _planOrderFormId: planOrderFormId } : {}),
        _workflow: {
          ...(data._workflow || {}),
          planOrderSavedAt: new Date().toISOString(),
        },
      };
      setData(next);
      writeToStorage(next);

      return { ok: true, caseId: caseId || data._caseId, planOrderFormId };
    } catch (err) {
      console.error('sendPlanOrder failed', err);
      return { ok: false, error: err.message || 'Failed to send', status: err.status };
    }
  }, [data]);

  // All the functions below are stable (useCallback), so the context value only
  // changes when the data or a status field changes. Memoizing it avoids handing
  // consumers a fresh object on unrelated re-renders of the provider.
  const value = useMemo(
    () => ({
      data,
      update,
      replace,
      reset,
      deleteRow,
      pendingUndo,
      undoDelete,
      saveNow,
      loadLatest,
      sendPlanOrder,
      stripKeysMatching,
      archivePq,
      autosaveStatus,
      saveError,
      saveErrorKind,
      loading,
      loadError,
      noReference,
      restoredLocalEdits,
      coedit,
      readOnly,
      planningMode,
      householdMode,
      quarantined,
    }),
    [
      data,
      update,
      replace,
      reset,
      deleteRow,
      pendingUndo,
      undoDelete,
      saveNow,
      loadLatest,
      sendPlanOrder,
      stripKeysMatching,
      archivePq,
      autosaveStatus,
      saveError,
      saveErrorKind,
      loading,
      loadError,
      noReference,
      restoredLocalEdits,
      coedit,
      readOnly,
      planningMode,
      householdMode,
      quarantined,
    ]
  );
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>');
  return ctx;
}

// Module-internal helpers, exported for tests only. View mode's guarantees live
// in these (not in the UI), so they need to be assertable directly.
export const __testables = {
  getReadOnlyFromUrl,
  getPlanningFromUrl,
  getHouseholdIdFromUrl,
  getPqLaunchFromUrl,
  getRecordIdFromUrl,
  readFromStorage,
  writeToStorage,
  // The assessment slice must never dirty the PQ, so that has to be assertable
  // against the real digest rather than by reading PQ_SLICE_KEYS in a test.
  pqDigest,
};

function stripDeep(obj, predicate) {
  if (Array.isArray(obj)) {
    return obj.map(item =>
      item && typeof item === 'object' ? stripDeep(item, predicate) : item
    );
  }
  if (!obj || typeof obj !== 'object') return obj;
  const next = {};
  for (const key of Object.keys(obj)) {
    if (predicate(key)) continue;
    const v = obj[key];
    next[key] = v && typeof v === 'object' ? stripDeep(v, predicate) : v;
  }
  return next;
}

function getByPath(obj, path) {
  return String(path)
    .split('.')
    .reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function setByPath(obj, path, value) {
  const keys = String(path).split('.');
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const existing = cursor[key];
    cursor[key] = existing && typeof existing === 'object' ? { ...existing } : {};
    cursor = cursor[key];
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}
